import type { BadgeTone } from '@/shared/components/ui/Badge';
import type { CurrentUser } from '@/shared/types/auth';
import type { PersonValue, ServiceDeskSummary, ServiceTicket, TicketPriority, TicketStatus } from './types';
import { isSuperadminRole } from '@/shared/permissions/roles';

/** Local User type compatible with AuthContext.User (department optional) */
export interface ServiceDeskUser {
  id?: string;
  role?: string | null;
  department?: string | null;
  /** Runs the service desk (the server says so: hr.servicedesk.manage). */
  operator?: boolean;
}

/** Departments are free text in this app ('IT', 'Customer Support'); compare normalised. */
const deptKey = (user?: ServiceDeskUser | null) =>
  String(user?.department ?? '').trim().toLowerCase().replace(/\s+/g, '_');

const ELEVATED_ROLES = new Set(['superadmin', 'admin', 'department_admin', 'manager', 'team_lead', 'owner']);
const SUPPORT_DEPARTMENTS = new Set(['support', 'customer_support']);
const ACTIVE_STATUSES = new Set<TicketStatus>(['open', 'triaged', 'assigned', 'in_progress', 'waiting_requester']);

export function idOf(value: PersonValue | undefined): string {
  if (!value) return '';
  if (typeof value === 'string') return value;
  return value.id ?? value._id ?? '';
}

export function personName(value: PersonValue | undefined, fallback = 'Unassigned'): string {
  if (!value) return fallback;
  if (typeof value === 'string') return value;
  return value.display_name || fallback;
}

export function queueForUser(user: ServiceDeskUser | null | undefined): 'all' | 'internal' | 'client_support' | null {
  const r = String(user?.role ?? '').toLowerCase();
  if (user?.operator || r === 'superadmin' || r === 'owner') return 'all';
  const dept = deptKey(user);
  if (dept === 'it') return 'internal';
  if (dept && SUPPORT_DEPARTMENTS.has(dept)) return 'client_support';
  return null;
}

export function isElevatedUser(user: ServiceDeskUser | null | undefined): boolean {
  const r = String(user?.role ?? '').toLowerCase();
  return !!user && (!!user.operator || ELEVATED_ROLES.has(r));
}

export function isItOperator(user: ServiceDeskUser | null | undefined): boolean {
  const r = String(user?.role ?? '').toLowerCase();
  return !!user?.operator || r === 'superadmin' || r === 'owner' || deptKey(user) === 'it';
}

export function canCreateClientSupport(user: ServiceDeskUser | null | undefined): boolean {
  const r = String(user?.role ?? '').toLowerCase();
  return !!user?.operator || r === 'superadmin' || r === 'owner' || (queueForUser(user) === 'client_support' && isElevatedUser(user));
}

export function canManageKnowledge(user: ServiceDeskUser | null | undefined): boolean {
  const r = String(user?.role ?? '').toLowerCase();
  return !!user?.operator || r === 'superadmin' || r === 'owner'
    || (isElevatedUser(user) && (deptKey(user) === 'it' || !!deptKey(user) && SUPPORT_DEPARTMENTS.has(deptKey(user))));
}

export interface TicketActionAvailability {
  assign: boolean;
  respond: boolean;
  waitForRequester: boolean;
  escalate: boolean;
  resolve: boolean;
  confirm: boolean;
}

export function ticketActionAvailability(
  user: ServiceDeskUser | null | undefined,
  ticket: ServiceTicket | null | undefined,
): TicketActionAvailability {
  const none = { assign: false, respond: false, waitForRequester: false, escalate: false, resolve: false, confirm: false };
  if (!user || !ticket) return none;
  const queue = queueForUser(user);
  const assignedToMe = idOf(ticket.assignedTo) === user.id;
  const requestedByMe = idOf(ticket.requesterUserId) === user.id;
  const canWork = !!user.operator || isSuperadminRole(user.role) || queue === ticket.kind || assignedToMe;
  const canAssign = !!user.operator || isSuperadminRole(user.role) || (isElevatedUser(user) && queue === ticket.kind);
  const terminal = ticket.status === 'resolved' || ticket.status === 'closed';
  return {
    assign: canAssign && ticket.status !== 'closed',
    respond: canWork && !terminal,
    waitForRequester: canWork && !terminal && ticket.status !== 'waiting_requester',
    escalate: canWork && !terminal && ticket.escalationLevel < 3,
    resolve: canWork && !terminal,
    confirm: (isSuperadminRole(user.role) || requestedByMe) && ticket.status === 'resolved',
  };
}

export function activeTicketCount(summary?: ServiceDeskSummary | null): number {
  if (!summary) return 0;
  return Object.entries(summary.statusCounts).reduce(
    (total, [status, count]) => total + (ACTIVE_STATUSES.has(status as TicketStatus) ? Number(count || 0) : 0),
    0,
  );
}

export function priorityTone(priority: TicketPriority): BadgeTone {
  if (priority === 'critical') return 'danger';
  if (priority === 'high') return 'warning';
  return 'neutral';
}

export function statusTone(status: TicketStatus | MonitoringStatus): BadgeTone {
  if (status === 'resolved' || status === 'closed') return 'success';
  if (status === 'waiting_requester' || status === 'monitoring') return 'warning';
  if (status === 'in_progress' || status === 'investigating') return 'info';
  return 'neutral';
}

type MonitoringStatus = 'open' | 'investigating' | 'monitoring' | 'resolved';

export function humanize(value: string): string {
  return value.replaceAll('_', ' ').replace(/\b\w/g, letter => letter.toUpperCase());
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short', timeZone: 'Asia/Kolkata' });
}

export function formatRemainingMinutes(minutes: number | null): string {
  if (minutes == null) return 'Deadline unavailable';
  const absolute = Math.abs(minutes);
  const hours = Math.floor(absolute / 60);
  const remainder = absolute % 60;
  const duration = hours ? `${hours}h ${remainder}m` : `${remainder}m`;
  return minutes < 0 ? `${duration} overdue` : `${duration} remaining`;
}
